import React from 'react';

import API from './api.js';
import Translations from './translations';
import { preprocessProject } from './preprocessor.js';
import messageReducer from './messageReducer.js';
import { PasswordInput, NicknameInput, Legal } from './authComponents.jsx';
import { SidePanel } from './sidePanel.jsx';
import { MainChatBox } from './mainChatBox.jsx';
import { makeScreenshot, createScrollToBottom } from './chatUtils.js';
import styles from './style.css';

/**
 * Chat window component
 */

const ChatContainer = ({ locale, showChat, setShowChat, vm }) => {
    const t = (s) => {
        if (Translations[locale])
            return Translations[locale][s];
        else
            return Translations["en"][s];
    }

    const [inputMessage, setInputMessage] = React.useState("")
    const [isResponding, setIsResponding] = React.useState(false)
    const [messages, dispatch] = React.useReducer(messageReducer, [])
    const [authRequired, setAuthRequired] = React.useState(false)
    const [nicknameRequired, setNicknameRequired] = React.useState(false)
    const [legalVisible, setLegalVisible] = React.useState(!document.cookie.includes("legalConfirm=true"))

    const outputRef = React.useRef(null)
    const scrollToBottom = createScrollToBottom(outputRef)

    const changeLastMessage = (content) => {
        dispatch({ type: 'changeLast', content });
    };

    const appendLastMessage = (delta) => {
        dispatch({ type: 'appendLast', delta });
    };


    const newConversation = () => {
        if (isResponding) return;
        dispatch({ type: 'reset', t: t });
        API.reconnect(
            (err) => {
                dispatch({ type: 'add', message: { author: "assistant", content: t("error") } });
                console.error("API error!", err);
            },
            (thinking) => changeLastMessage(`_${thinking}_`),
            appendLastMessage,
            (content) => { changeLastMessage(content); setIsResponding(false); },
            (authUpdate) => {
                setAuthRequired(!authUpdate.isAuthorized);
                setIsResponding(false);
            },
            locale,
            () => {
                setNicknameRequired(true);
            }
        );
    };

    const send = async () => {
        const msg = inputMessage;
        setInputMessage("");
        if (!msg.trim() || isResponding) return; // no empty message
        setIsResponding(true);
        dispatch({ type: 'add', message: { author: "user", content: msg } });
        dispatch({ type: 'add', message: { author: "assistant", content: `_${t('processing')}_` } });
        try {
            API.send(msg, vm.editingTarget.sprite.name, preprocessProject(JSON.parse(vm.toJSON())), await makeScreenshot(vm));
            setTimeout(scrollToBottom(true), 500)
        } catch (err) {
            setIsResponding(false)
            throw err
        }
    };

    const passwordSend = () => {
        setIsResponding(true);
        const password = inputMessage;
        setInputMessage("");
        API.authorize(password);
    }

    const nicknameSend = () => {
        const nickname = inputMessage;
        setInputMessage("");
        setIsResponding(false);
        setNicknameRequired(false);
        API.sendNickname(nickname);
    }


    React.useEffect(() => {
        newConversation();
    }, [])


    React.useEffect(scrollToBottom, [messages]);
    React.useEffect(() => {
        if (showChat)
            scrollToBottom(true)
    }, [showChat])

    return !showChat ? (<></>) : (
        <div className={styles.container}>

            {legalVisible ?
                <Legal t={t} setLegalVisible={setLegalVisible}></Legal>
            :
            authRequired ?
                <PasswordInput
                    t={t}
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    isResponding={isResponding}
                    onSend={passwordSend}
                />
                :
                nicknameRequired ?
                    <NicknameInput
                        t={t}
                        inputMessage={inputMessage}
                        setInputMessage={setInputMessage}
                        isResponding={isResponding}
                        onSend={nicknameSend}
                    />
                    :
                    <MainChatBox
                        messages={messages}
                        dispatch={dispatch}
                        outputRef={outputRef}
                        inputMessage={inputMessage}
                        setInputMessage={setInputMessage}
                        isResponding={isResponding}
                        t={t}
                        send={send}
                    />
            }

            <SidePanel
                buttonsHidden={authRequired || nicknameRequired || legalVisible}
                setShowChat={setShowChat}
                isResponding={isResponding}
                newConversation={newConversation}
                send={send}
            />
        </div>
    )
}

export default ChatContainer;